import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, formatPrice } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { useCustomer } from "@/context/CustomerContext";
import { AddressFields, shopBtn, shopInput } from "@/components/shop/AddressFields";

const Section = ({ n, title, children }) => (
  <section className="py-8 border-b border-[#D5B98C]/50">
    <h2 className="font-serif text-2xl text-[#39251D] mb-5"><span className="text-[#956842] mr-2">{n}.</span>{title}</h2>
    {children}
  </section>
);

export default function CheckoutPage() {
  const { cart, clearCart } = useStore();
  const { customer } = useCustomer();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "", phone: "", notes: "" });
  const [addr, setAddr] = useState({ country: "IT" });
  const [method, setMethod] = useState("");
  const [terms, setTerms] = useState(false);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const items = cart.map((i) => ({ slug: i.slug, qty: i.qty, variant: i.variant }));

  useEffect(() => { api.get("/shop/config").then((r) => { setCfg(r.data); setMethod(r.data.paypal_client_id ? "paypal" : r.data.bank_enabled ? "bonifico" : ""); }); }, []);
  useEffect(() => {
    if (!customer) return;
    setForm((f) => ({ ...f, email: customer.email, first_name: customer.first_name, last_name: customer.last_name, phone: customer.phone || f.phone }));
    if (customer.addresses?.[0]) setAddr(customer.addresses[0]);
  }, [customer]);
  useEffect(() => {
    if (!cart.length) return;
    api.post("/shop/quote", { items, country: addr.country || "IT" }).then((r) => setQuote(r.data)).catch((e) => toast.error(apiError(e)));
  }, [cart, addr.country]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!cart.length) return (
    <div className="max-w-xl mx-auto px-5 pt-20 text-center" data-testid="checkout-empty">
      <h1 className="font-serif text-4xl text-[#39251D]">Il carrello è vuoto</h1>
      <Link to="/collezione" className="inline-block mt-6 text-xs uppercase tracking-[0.2em] border-b border-[#956842] pb-1">Scopri la collezione</Link>
    </div>
  );

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!method) return toast.error("Nessun metodo di pagamento disponibile");
    setBusy(true);
    try {
      const { data } = await api.post("/orders", { ...form, items, address: { ...addr, phone: form.phone }, payment_method: method, terms_accepted: terms });
      clearCart();
      navigate(`/ordine/${data.number}?t=${data.token}`);
    } catch (err) { toast.error(apiError(err)); setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="max-w-[1200px] mx-auto px-5 sm:px-8 pt-10 grid lg:grid-cols-[1fr_400px] gap-10" data-testid="checkout-page">
      <div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#39251D]">Checkout</h1>
        {!customer && <p className="mt-3 text-sm text-[#242321]/70">Acquisti come ospite. Hai un account? <Link to="/account?next=/checkout" data-testid="checkout-login-link" className="underline">Accedi</Link></p>}
        <Section n="1" title="I tuoi dati">
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="checkout-first-name" required placeholder="Nome" value={form.first_name} onChange={set("first_name")} className={shopInput} />
            <input data-testid="checkout-last-name" required placeholder="Cognome" value={form.last_name} onChange={set("last_name")} className={shopInput} />
            <input data-testid="checkout-email" required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={shopInput} />
            <input data-testid="checkout-phone" required minLength={6} placeholder="Telefono (per il corriere)" value={form.phone} onChange={set("phone")} className={shopInput} />
          </div>
        </Section>
        <Section n="2" title="Indirizzo di spedizione">
          {cfg && <AddressFields value={addr} onChange={setAddr} countries={cfg.countries} prefix="checkout" />}
          <p className="mt-3 text-xs text-[#242321]/60">Spediamo con BRT in Italia e nei Paesi UE elencati. Preparazione 1–2 giorni lavorativi. <Link to="/spedizioni" className="underline">Dettagli</Link></p>
        </Section>
        <Section n="3" title="Pagamento">
          {cfg && !cfg.paypal_client_id && !cfg.bank_enabled && <p data-testid="checkout-no-payment" className="text-sm text-[#8a2f22]">Al momento nessun metodo di pagamento è attivo. Riprova più tardi o contattaci.</p>}
          <div className="space-y-2">
            {cfg?.paypal_client_id && (
              <label className={`flex gap-3 p-4 border cursor-pointer ${method === "paypal" ? "border-[#39251D] bg-white/70" : "border-[#D5B98C]"}`}>
                <input type="radio" data-testid="checkout-method-paypal" checked={method === "paypal"} onChange={() => setMethod("paypal")} />
                <span><b className="block text-sm">PayPal o carta di debito/credito</b><span className="text-xs text-[#242321]/65">Paga con il conto PayPal oppure con qualsiasi carta, anche senza conto PayPal{cfg.paypal_mode === "sandbox" ? " · modalità di prova (sandbox)" : ""}</span></span>
              </label>
            )}
            {cfg?.bank_enabled && (
              <label className={`flex gap-3 p-4 border cursor-pointer ${method === "bonifico" ? "border-[#39251D] bg-white/70" : "border-[#D5B98C]"}`}>
                <input type="radio" data-testid="checkout-method-bonifico" checked={method === "bonifico"} onChange={() => setMethod("bonifico")} />
                <span><b className="block text-sm">Bonifico bancario</b><span className="text-xs text-[#242321]/65">Coordinate dopo l'ordine · pagamento entro 24 ore, altrimenti l'ordine viene annullato · spedizione dopo l'accredito</span></span>
              </label>
            )}
          </div>
          <textarea data-testid="checkout-notes" placeholder="Note per l'ordine (facoltative)" value={form.notes} onChange={set("notes")} rows={2} className={`${shopInput} mt-4`} />
        </Section>
      </div>
      <aside className="lg:sticky lg:top-32 self-start bg-white/60 border border-[#D5B98C]/60 p-6" data-testid="checkout-summary">
        <h2 className="font-serif text-2xl text-[#39251D] mb-4">Riepilogo</h2>
        <ul className="divide-y divide-[#D5B98C]/40">
          {cart.map((i) => (
            <li key={i.key} className="flex gap-3 py-3 text-sm">
              <img src={i.image} alt="" className="w-14 h-16 object-cover" />
              <div className="flex-1"><div className="font-serif text-base text-[#39251D]">{i.name}</div><div className="text-xs text-[#242321]/60">{i.variant ? `${i.variant} · ` : ""}Qtà {i.qty}</div></div>
              <span>{formatPrice(i.price * i.qty)}</span>
            </li>
          ))}
        </ul>
        {quote && (
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotale prodotti</span><span data-testid="checkout-subtotal">{formatPrice(quote.subtotal)}</span></div>
            <div className="flex justify-between"><span>Spedizione</span><span data-testid="checkout-shipping">{quote.available ? (quote.shipping === 0 ? "Gratuita" : formatPrice(quote.shipping)) : "Non disponibile"}</span></div>
            <p className="text-[11px] text-[#242321]/55">Gratuita da {formatPrice(addr.country === "IT" ? cfg?.shipping.it_free_from : cfg?.shipping.eu_free_from)} di prodotti, calcolata sul valore dei prodotti.</p>
            <div className="flex justify-between pt-3 border-t border-[#D5B98C]/50 text-lg"><b>Totale</b><b data-testid="checkout-total">{quote.total != null ? formatPrice(quote.total) : "—"}</b></div>
            <p className="text-[11px] text-[#242321]/55">Prezzi in euro, IVA inclusa ove applicabile.</p>
          </div>
        )}
        <label className="flex gap-2 mt-5 text-xs leading-relaxed">
          <input type="checkbox" data-testid="checkout-terms" checked={terms} onChange={(e) => setTerms(e.target.checked)} required className="mt-0.5" />
          <span>Ho letto e accetto i <Link to="/termini" target="_blank" className="underline">Termini e condizioni di vendita</Link> e ho preso visione della <Link to="/privacy" target="_blank" className="underline">Privacy Policy</Link> e delle informazioni su <Link to="/resi" target="_blank" className="underline">recesso e resi</Link>.</span>
        </label>
        <button data-testid="checkout-submit" disabled={busy || !method || !quote?.available || !terms} className={`${shopBtn} w-full mt-5`}>{busy ? "Invio…" : "Ordina e paga"}</button>
        <p className="mt-2 text-[11px] text-[#242321]/55 text-center">L'invio dell'ordine comporta l'obbligo di pagamento.</p>
      </aside>
    </form>
  );
}
